import React, { useState, useEffect, useMemo } from 'react';
import { Holding } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  holdings: Holding[];
  selectedSymbol?: string;
}

// Helper to generate normally distributed random numbers (Box-Muller transform)
const randomNormal = () => {
  let u = 0, v = 0;
  while(u === 0) u = Math.random(); 
  while(v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
};

export const MonteCarloSimulation: React.FC<Props> = ({ holdings, selectedSymbol: propSymbol }) => {
  const { language } = useLanguage();
  // Available assets for simulation
  const availableAssets = useMemo(() => {
    const symbols = holdings.map(h => ({
      symbol: h.symbol,
      price: h.current_price || h.avg_cost || 100
    }));
    
    // Add default benchmarks if not present
    if (!symbols.some(s => s.symbol === 'CSG')) symbols.push({ symbol: 'CSG', price: 148.50 });
    if (!symbols.some(s => s.symbol === 'SPY')) symbols.push({ symbol: 'SPY', price: 540.20 });
    if (!symbols.some(s => s.symbol === 'BTC')) symbols.push({ symbol: 'BTC', price: 64200.00 });
    if (!symbols.some(s => s.symbol === 'GLD')) symbols.push({ symbol: 'GLD', price: 215.10 });
    
    return symbols;
  }, [holdings]);

  const [selectedSymbol, setSelectedSymbol] = useState('CSG');
  
  // Sync prop symbol changes to internal state
  useEffect(() => {
    if (propSymbol) {
      setSelectedSymbol(propSymbol);
    }
  }, [propSymbol]);

  const [horizonYears, setHorizonYears] = useState<number>(3);
  const [pathCount, setPathCount] = useState<number>(50);
  
  // Simulation results
  const [paths, setPaths] = useState<number[][]>([]);
  const [stepLimit, setStepLimit] = useState<number>(0);
  const [isSimulating, setIsSimulating] = useState<boolean>(false);

  // Asset parameters (drift & volatility)
  const assetParams = useMemo(() => {
    const params: Record<string, { drift: number; vol: number }> = {
      'CSG': { drift: 0.14, vol: 0.22 },
      'SPY': { drift: 0.09, vol: 0.14 },
      'BTC': { drift: 0.28, vol: 0.48 },
      'GLD': { drift: 0.05, vol: 0.12 },
    };
    return params[selectedSymbol] || { drift: 0.10, vol: 0.20 };
  }, [selectedSymbol]);

  const selectedAssetPrice = useMemo(() => {
    const found = availableAssets.find(a => a.symbol === selectedSymbol);
    return found ? found.price : 100;
  }, [availableAssets, selectedSymbol]);

  const stepsPerYear = 12; // Monthly steps
  const totalSteps = horizonYears * stepsPerYear;

  // Run simulation calculation
  const runSimulation = () => {
    setIsSimulating(true);
    setStepLimit(0);

    const drift = assetParams.drift;
    const vol = assetParams.vol;
    const dt = 1 / stepsPerYear;
    const S0 = selectedAssetPrice;

    const newPaths: number[][] = [];
    for (let p = 0; p < pathCount; p++) {
      const path: number[] = [S0];
      let currentPrice = S0;
      for (let s = 1; s <= totalSteps; s++) {
        const rand = randomNormal();
        const exponent = (drift - 0.5 * Math.pow(vol, 2)) * dt + vol * Math.sqrt(dt) * rand;
        currentPrice = currentPrice * Math.exp(exponent);
        path.push(currentPrice);
      }
      newPaths.push(path);
    }

    setPaths(newPaths);
  };

  // Run simulation on mount or when asset changes
  useEffect(() => {
    runSimulation();
  }, [selectedSymbol, horizonYears, pathCount, selectedAssetPrice]);

  // Animate path drawing from left to right
  useEffect(() => {
    if (paths.length === 0) return;
    
    if (stepLimit < totalSteps) {
      const timer = setTimeout(() => {
        setStepLimit(prev => Math.min(prev + 1, totalSteps));
      }, 35);
      return () => clearTimeout(timer);
    } else {
      setIsSimulating(false);
    }
  }, [stepLimit, paths, totalSteps]);

  // Quant metrics calculations based on ending values of the simulated paths
  const stats = useMemo(() => {
    if (paths.length === 0 || paths[0].length === 0) return null;
    
    // Extract ending prices for each path
    const endingPrices = paths.map(p => p[totalSteps]);
    const sortedPrices = [...endingPrices].sort((a, b) => a - b);
    
    const initialPrice = selectedAssetPrice;
    
    // Median
    const medianEnd = sortedPrices[Math.floor(sortedPrices.length * 0.5)];
    const medianReturn = ((medianEnd - initialPrice) / initialPrice) * 100;
    
    // VaR (95% confidence level -> 5th percentile)
    const var95End = sortedPrices[Math.max(0, Math.floor(sortedPrices.length * 0.05))];
    const var95Pct = ((initialPrice - var95End) / initialPrice) * 100;

    // Expected Shortfall (CVaR - average of worst 5% outcomes)
    const worstOutcomesCount = Math.max(1, Math.floor(sortedPrices.length * 0.05));
    const worstOutcomesSum = sortedPrices.slice(0, worstOutcomesCount).reduce((sum, val) => sum + val, 0);
    const cvarEnd = worstOutcomesSum / worstOutcomesCount;
    const cvarPct = ((initialPrice - cvarEnd) / initialPrice) * 100;

    // Upside Potential (90th percentile)
    const upsideEnd = sortedPrices[Math.floor(sortedPrices.length * 0.90)];
    const upsidePct = ((upsideEnd - initialPrice) / initialPrice) * 100;

    // Probability of Positive Return
    const positivePaths = endingPrices.filter(p => p > initialPrice).length;
    const probPositive = (positivePaths / pathCount) * 100;

    return {
      medianEnd,
      medianReturn,
      var95End,
      var95Pct: Math.max(0, var95Pct),
      cvarEnd,
      cvarPct: Math.max(0, cvarPct),
      upsideEnd,
      upsidePct,
      probPositive
    };
  }, [paths, totalSteps, selectedAssetPrice, pathCount]);

  // Chart configuration & path conversion to SVG coordinates
  const chartWidth = 500;
  const chartHeight = 180;
  const paddingLeft = 45;
  const paddingRight = 15;
  const paddingTop = 15;
  const paddingBottom = 25;

  const svgCoordinates = useMemo(() => {
    if (paths.length === 0) return { lines: [], percentiles: null, yTicks: [] };

    // Find min and max price across all paths to scale the Y-axis
    let minPrice = selectedAssetPrice;
    let maxPrice = selectedAssetPrice;

    paths.forEach(p => {
      p.forEach((price, step) => {
        if (step <= stepLimit) {
          if (price < minPrice) minPrice = price;
          if (price > maxPrice) maxPrice = price;
        }
      });
    });

    // Add padding
    minPrice = Math.max(0, minPrice * 0.9);
    maxPrice = maxPrice * 1.1;

    const scaleX = (step: number) => {
      return paddingLeft + (step / totalSteps) * (chartWidth - paddingLeft - paddingRight);
    };

    const scaleY = (price: number) => {
      return chartHeight - paddingBottom - ((price - minPrice) / (maxPrice - minPrice)) * (chartHeight - paddingTop - paddingBottom);
    };

    // Build SVG paths
    const lines = paths.map((path, idx) => {
      let d = '';
      for (let s = 0; s <= stepLimit; s++) {
        const x = scaleX(s);
        const y = scaleY(path[s]);
        d += `${s === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)} `;
      }
      
      const isUp = path[stepLimit] > selectedAssetPrice;
      return {
        d,
        color: isUp ? 'rgba(21, 128, 61, 0.15)' : 'rgba(185, 28, 28, 0.15)',
        isMedian: false
      };
    });

    // Compute percentile paths over time (specifically 10th, 50th, 90th)
    const medianCoords: [number, number][] = [];
    const lowerCoords: [number, number][] = []; // 10th percentile
    const upperCoords: [number, number][] = []; // 90th percentile

    for (let s = 0; s <= stepLimit; s++) {
      const stepPrices = paths.map(p => p[s]).sort((a, b) => a - b);
      const medianVal = stepPrices[Math.floor(stepPrices.length * 0.5)];
      const lowerVal = stepPrices[Math.floor(stepPrices.length * 0.1)];
      const upperVal = stepPrices[Math.floor(stepPrices.length * 0.9)];

      medianCoords.push([scaleX(s), scaleY(medianVal)]);
      lowerCoords.push([scaleX(s), scaleY(lowerVal)]);
      upperCoords.push([scaleX(s), scaleY(upperVal)]);
    }

    // Convert coordinates to SVG path strings
    const medianD = medianCoords.length > 0
      ? 'M ' + medianCoords.map(c => `${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' L ')
      : '';

    // Shaded area path (Upper percentile down to lower percentile)
    let shadedD = '';
    if (upperCoords.length > 0 && lowerCoords.length > 0) {
      const forward = upperCoords.map(c => `${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' L ');
      const backward = [...lowerCoords].reverse().map(c => `${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' L ');
      shadedD = `M ${forward} L ${backward} Z`;
    }

    // Y-Axis Ticks
    const tickCount = 4;
    const yTicks = Array.from({ length: tickCount }).map((_, idx) => {
      const val = minPrice + (idx / (tickCount - 1)) * (maxPrice - minPrice);
      return {
        value: val,
        y: scaleY(val)
      };
    });

    return {
      lines,
      percentiles: {
        medianD,
        shadedD
      },
      yTicks,
      scaleX
    };
  }, [paths, stepLimit, selectedAssetPrice, totalSteps]);

  return (
    <div className="glass-panel p-6 flex flex-col md:flex-row gap-6 font-mono text-xs">
      {/* Controls & Metrics Panel */}
      <div className="w-full md:w-64 space-y-4 shrink-0 border-r border-[#1c1917]/20 pr-6">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917]">
            {language === 'sk' ? 'Simulácia Monte Carlo' : 'Monte Carlo Simulation'}
          </h3>
          <div className="text-[10px] text-stone-500 mt-1">
            {language === 'sk' ? 'Viac-cestná projekcia portfólia' : 'Multi-Path Portfolio Projection'}
          </div>
        </div>

        {/* Configuration controls */}
        <div className="space-y-3 pt-2">
          <div>
            <label className="text-[10px] text-stone-500 block mb-1">
              {language === 'sk' ? 'ZVOLIŤ TICKER' : 'SELECT TICKER'}
            </label>
            <select
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              className="w-full bg-[#eae6db] border border-[#1c1917] rounded px-2 py-1 font-bold text-[#1c1917] outline-none"
            >
              {availableAssets.map(a => (
                <option key={a.symbol} value={a.symbol}>{a.symbol} (${Number(a.price).toFixed(2)})</option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-stone-500 block mb-1">
                {language === 'sk' ? 'HORIZONT' : 'HORIZON'}
              </label>
              <select
                value={horizonYears}
                onChange={(e) => setHorizonYears(Number(e.target.value))}
                className="w-full bg-[#eae6db] border border-[#1c1917] rounded px-2 py-1 font-bold text-[#1c1917] outline-none"
              >
                <option value={1}>{language === 'sk' ? '1 Rok' : '1 Year'}</option>
                <option value={2}>{language === 'sk' ? '2 Roky' : '2 Years'}</option>
                <option value={3}>{language === 'sk' ? '3 Roky' : '3 Years'}</option>
                <option value={5}>{language === 'sk' ? '5 Rokov' : '5 Years'}</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-stone-500 block mb-1">
                {language === 'sk' ? 'POČET CIEST' : 'PATHS'}
              </label>
              <select
                value={pathCount}
                onChange={(e) => setPathCount(Number(e.target.value))}
                className="w-full bg-[#eae6db] border border-[#1c1917] rounded px-2 py-1 font-bold text-[#1c1917] outline-none"
              >
                <option value={10}>{language === 'sk' ? '10 ciest' : '10 paths'}</option>
                <option value={50}>{language === 'sk' ? '50 ciest' : '50 paths'}</option>
                <option value={100}>{language === 'sk' ? '100 ciest' : '100 paths'}</option>
              </select>
            </div>
          </div>

          <button
            onClick={runSimulation}
            disabled={isSimulating}
            className="w-full text-center bg-[#1c1917] text-[#fbfaf7] hover:bg-stone-800 disabled:bg-stone-400 font-extrabold uppercase py-2 px-3 border border-[#1c1917] rounded tracking-wider transition-all"
          >
            {isSimulating 
              ? (language === 'sk' ? 'SIMULUJEM...' : 'SIMULATING...') 
              : (language === 'sk' ? 'SPUSTIŤ SIMULÁCIU' : 'RUN SIMULATION')}
          </button>
        </div>

        {/* Quant risk metrics */}
        {stats && (
          <div className="space-y-2 pt-2 border-t border-[#1c1917]/10">
            <div className="flex justify-between border-b border-stone-200 pb-1">
              <span className="text-stone-500">{language === 'sk' ? 'OČAKÁVANÝ MEDIÁN' : 'MEDIAN EXPECTED'}</span>
              <span className={`font-bold ${stats.medianReturn >= 0 ? 'text-[#15803d]' : 'text-[#b91c1c]'}`}>
                {stats.medianReturn >= 0 ? '+' : ''}{stats.medianReturn.toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between border-b border-stone-200 pb-1">
              <span className="text-stone-500">{language === 'sk' ? 'HODNOTA V RIZIKU (95%)' : 'VALUE AT RISK (95%)'}</span>
              <span className="font-bold text-[#b91c1c]">{stats.var95Pct.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between border-b border-stone-200 pb-1">
              <span className="text-stone-500">{language === 'sk' ? 'OČAKÁVANÝ STRATA' : 'EXPECTED SHORTFALL'}</span>
              <span className="font-bold text-[#b91c1c]">{stats.cvarPct.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between border-b border-stone-200 pb-1">
              <span className="text-stone-500">{language === 'sk' ? 'POTENCIÁL (90. PCT)' : 'UPSIDE (90TH PCT)'}</span>
              <span className="font-bold text-[#15803d]">+{stats.upsidePct.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between border-b border-stone-200 pb-1">
              <span className="text-stone-500">{language === 'sk' ? 'PRAVDEPODOBNOSŤ ZISKU' : 'PROB. OF PROFIT'}</span>
              <span className="font-bold text-[#15803d]">{stats.probPositive.toFixed(0)}%</span>
            </div>
          </div>
        )}
      </div>

      {/* SVG Canvas Area */}
      <div className="flex-1 flex flex-col justify-between min-h-[220px]">
        <div className="flex justify-between items-center text-[10px] text-stone-500 mb-2">
          <span>TICKER: {selectedSymbol} • DRIFT {Math.round(assetParams.drift * 100)}% • VOL {Math.round(assetParams.vol * 100)}%</span>
          <span>{language === 'sk' ? 'GEOMETRICKÝ BROWNOV POHYB (GBM)' : 'GEOMETRIC BROWNIAN MOTION (GBM)'}</span>
        </div>

        <div className="flex-1 min-h-[160px] relative border border-[#1c1917]/10 bg-[#fbfaf7] rounded p-2 overflow-hidden">
          <svg className="w-full h-full" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none">
            {/* Horizontal Grid lines & Y axis ticks */}
            {svgCoordinates.yTicks.map((tick, idx) => (
              <g key={idx}>
                <line
                  x1={paddingLeft}
                  y1={tick.y}
                  x2={chartWidth - paddingRight}
                  y2={tick.y}
                  stroke="#1c1917"
                  strokeWidth="0.5"
                  strokeDasharray="4,4"
                  opacity="0.1"
                />
                <text
                  x={paddingLeft - 8}
                  y={tick.y + 3}
                  textAnchor="end"
                  className="text-[7px] font-semibold fill-stone-500"
                >
                  ${tick.value.toFixed(0)}
                </text>
              </g>
            ))}

            {/* Vertical Time steps grids */}
            {Array.from({ length: horizonYears + 1 }).map((_, idx) => {
              const step = idx * stepsPerYear;
              const x = svgCoordinates.scaleX ? svgCoordinates.scaleX(step) : paddingLeft;
              return (
                <g key={idx}>
                  <line
                    x1={x}
                    y1={paddingTop}
                    x2={x}
                    y2={chartHeight - paddingBottom}
                    stroke="#1c1917"
                    strokeWidth="0.5"
                    strokeDasharray="4,4"
                    opacity="0.1"
                  />
                  <text
                    x={x}
                    y={chartHeight - paddingBottom + 12}
                    textAnchor="middle"
                    className="text-[7px] font-semibold fill-stone-500"
                  >
                    {idx === 0 ? (language === 'sk' ? 'Štart' : 'Start') : `${language === 'sk' ? 'Rok' : 'Yr'} ${idx}`}
                  </text>
                </g>
              );
            })}

            {/* Shaded confidence interval band */}
            {svgCoordinates.percentiles?.shadedD && (
              <path
                d={svgCoordinates.percentiles.shadedD}
                fill="rgba(28, 25, 23, 0.05)"
                stroke="none"
              />
            )}

            {/* Main Simulated Paths */}
            {svgCoordinates.lines.map((line, idx) => (
              <path
                key={idx}
                d={line.d}
                fill="none"
                stroke={line.color}
                strokeWidth="0.75"
              />
            ))}

            {/* Median path */}
            {svgCoordinates.percentiles?.medianD && (
              <path
                d={svgCoordinates.percentiles.medianD}
                fill="none"
                stroke="#1c1917"
                strokeWidth="1.75"
              />
            )}

            {/* Horizontal line for Starting Price reference */}
            {svgCoordinates.scaleX && (
              <line
                x1={paddingLeft}
                y1={chartHeight - paddingBottom - ((selectedAssetPrice - svgCoordinates.yTicks[0]?.value) / (svgCoordinates.yTicks[svgCoordinates.yTicks.length-1]?.value - svgCoordinates.yTicks[0]?.value)) * (chartHeight - paddingTop - paddingBottom)}
                x2={chartWidth - paddingRight}
                y2={chartHeight - paddingBottom - ((selectedAssetPrice - svgCoordinates.yTicks[0]?.value) / (svgCoordinates.yTicks[svgCoordinates.yTicks.length-1]?.value - svgCoordinates.yTicks[0]?.value)) * (chartHeight - paddingTop - paddingBottom)}
                stroke="#1c1917"
                strokeWidth="0.5"
                strokeDasharray="2,2"
                opacity="0.4"
              />
            )}
          </svg>
        </div>
      </div>
    </div>
  );
};
